import React from 'react';
import PropTypes from 'prop-types';
import { formatElapsedTime } from '../../../utils/quizUtils';
import { QuestionImage } from '../shared';

const QuizResultsSummary = ({ 
  sessionResponses, 
  questions, 
  startTime, 
  endTime,
  onClose 
}) => {
  const calculateElapsedTime = () => {
    if (!startTime || startTime <= 0) {
      return "計測不可";
    }
    
    // endTimeがある場合は固定時間、ない場合はリアルタイム計算（後方互換性）
    const finalTime = endTime || Date.now();
    const elapsedMs = finalTime - startTime;
    
    if (elapsedMs < 0) {
      return "計測不可";
    }
    return formatElapsedTime(elapsedMs);
  };

  return (
    <div className="p-4 mt-4 bg-pink-50 rounded border border-pink-200">
      <h3 className="text-xl font-semibold text-purple-700 mb-4">全問題の結果</h3>

      {/* 正答率サマリー */}
      <div className="mb-6 p-4 bg-white rounded shadow">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-purple-600">
            正答率: {(Object.values(sessionResponses).filter(r => r.isCorrect).length / questions.length * 100).toFixed(1)}%
          </div>
          <div className="text-purple-600">
            正解数: {Object.values(sessionResponses).filter(r => r.isCorrect).length}/{questions.length}
          </div>
          <div className="text-purple-600">
            所要時間: {calculateElapsedTime()}
          </div>
        </div>
      </div>

      {/* 各問題の詳細結果 */}
      <div className="space-y-4">
        {questions.map((question, index) => {
          const response = sessionResponses[question.id];
          if (!response) return null;

          return (
            <div key={question.id} className="bg-white p-4 rounded-lg shadow">
              {/* 問題番号と正誤表示 */}
              <div className="mb-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">問題 {index + 1}</span>
                  <span className={`font-semibold ${response.isCorrect ? 'text-blue-600' : 'text-red-600'}`}>
                    {response.isCorrect ? '✓ 正解' : 
                     (response.explanation === "時間切れです。" ? "× 時間切れ" : "× 不正解")}
                  </span>
                </div>
              </div>

              {/* 問題の詳細表示 */}
              <div className="space-y-2">
                {/* 画像表示（存在する場合） */}
                {question.hasImage && question.questionImage && (
                  <div className="mb-3">
                    <QuestionImage 
                      imageData={question.questionImage} 
                      alt={`問題 ${index + 1} の画像`}
                      className="max-w-sm"
                    />
                  </div>
                )}

                {/* 問題文 */}
                {question.type !== 'sentence-rearrangement' && question.question && (
                  <p className="text-gray-800">
                    <span className="font-medium">問題: </span>
                    {question.question}
                  </p>
                )}

                {/* 問題タイプ別の詳細表示 */}
                {question.type === 'multiple-choice' && (
                  <div className="ml-4">
                    <p className="text-sm text-gray-600">選択肢:</p>
                    <ul className="list-disc ml-4">
                      {question.choices.map((choice, idx) => (
                        <li key={idx} className={`
                          ${choice === question.answer ? 'text-blue-600 font-semibold' : ''}
                          ${choice === response.userAnswer && choice !== question.answer ? 'text-green-600' : ''}
                        `}>
                          {choice}
                          {choice === question.answer && ' (正解)'}
                          {choice === response.userAnswer && choice !== question.answer && ' (あなたの回答)'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {question.type === 'sentence-rearrangement' && (
                  <>
                    <p className="text-gray-700 mb-2">
                      <span className="font-medium">問題: </span>
                      {question.japaneseTranslation}
                    </p>
                    <p className="mb-2">
                      <span className="font-medium text-gray-700">正解: </span>
                      <span className="text-blue-600">{question.englishSentence}</span>
                    </p>
                    <p>
                      <span className="font-medium text-gray-700">あなたの回答: </span>
                      <span className="text-green-600">
                        {response.explanation === "時間切れです。" ? 
                          "時間切れ" : 
                          (response.userAnswer || '回答なし')}
                      </span>
                    </p>
                  </>
                )}

                {question.type === 'true-false' && (
                  <>
                    <p>
                      <span className="font-medium text-gray-700">正解: </span>
                      <span className="text-blue-600">{question.answer ? '正しい' : '誤り'}</span>
                    </p>
                    <p>
                      <span className="font-medium text-gray-700">あなたの回答: </span>
                      <span className="text-green-600">
                        {response.explanation === "時間切れです。" ? 
                          "時間切れ" : 
                          (response.userAnswer ? '正しい' : '誤り')}
                      </span>
                    </p>
                  </>
                )}

                {question.type === 'fill-in-the-blank' && (
                  <>
                    <p>
                      <span className="font-medium text-gray-700">正解: </span>
                      <span className="text-blue-600">{question.answer}</span>
                    </p>
                    <p>
                      <span className="font-medium text-gray-700">あなたの回答: </span>
                      <span className="text-green-600">
                        {response.explanation === "時間切れです。" ? 
                          "時間切れ" : 
                          response.userAnswer}
                      </span>
                    </p>
                  </>
                )}

                {question.type === 'matching' && (
                  <>
                    <p className="font-medium text-gray-700 mb-2">マッチング:</p>
                    <div className="ml-4">
                      {question.leftItems.map((leftItem, idx) => (
                        <div key={idx} className="flex gap-4">
                          <span>{leftItem}</span>
                          <span>→</span>
                          <span>
                            {response.explanation === "時間切れです。" ? 
                              "時間切れ" : (
                                <>
                                  <span className="text-green-600">
                                    {question.rightItems[response.userAnswer?.[idx] || 0]}
                                  </span>
                                  {question.correctMatches[idx] !== response.userAnswer?.[idx] && 
                                    <span className="text-blue-600">
                                      {` (正解: ${question.rightItems[question.correctMatches[idx]]})`}
                                    </span>
                                  }
                                </>
                              )
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* 解説 */}
                {response.explanation && (
                  <div className="mt-2 text-gray-600 bg-gray-50 p-2 rounded">
                    <span className="font-medium">解説: </span>
                    {response.explanation === "時間切れです。" ? 
                      "時間切れのため、解答できませんでした。" : 
                      response.explanation}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 閉じるボタンのコンテナ */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
          aria-label="結果を閉じるボタン"
        >
          閉じる
        </button>
      </div>
    </div>
  );
};

QuizResultsSummary.propTypes = {
  sessionResponses: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  startTime: PropTypes.number.isRequired,
  endTime: PropTypes.number,
  onClose: PropTypes.func.isRequired,
};

export default QuizResultsSummary; 